import { ChangeEngine } from '../../../src/engine/ChangeEngine';
import type { IAuditLog } from '../../../src/engine/AuditLog';
import type { IBatchResult } from '../../../src/models/Operation';
import type { IOrganizationPlan, IPlannedChange } from '../../../src/models/Plan';
import { resolveChange } from '../../../src/plan/PlanSimulator';
import { GraphService, type IGraphClient } from '../../../src/services/GraphService';
import type { IWorkspace } from '../../api/src/workspace';
import { getItemByPath, getRoot } from '../graphx';
import { miniScan } from '../filing/inbox';

/** Folder names can't hold some characters; people's names sometimes do. */
export function folderName(s: string): string {
  return s.replace(/["*:<>?/\\|#%]/g, ' ').replace(/\s+/g, ' ').trim().replace(/[. ]+$/, '').slice(0, 120) || 'Unnamed';
}

/**
 * Makes sure these folders exist in the library (paths from its top, e.g. "Clients/Khalil, Omar/…"),
 * through the change engine: checked first, each new folder logged in History and removed by Undo
 * while still empty. Folders that are already there are left as they are.
 */
export async function ensureFolders(graph: IGraphClient, workspace: IWorkspace, audit: IAuditLog, user: string, paths: string[]): Promise<IBatchResult | undefined> {
  const missing: string[] = [];
  for (const p of paths) {
    if (!(await getItemByPath(graph, workspace.driveId, p))) {
      missing.push(p);
    }
  }
  if (!missing.length) {
    return undefined;
  }
  const root = await getRoot(graph, workspace.driveId);
  const scan = miniScan(workspace, root.id, { id: root.id, path: '' }, []);
  const changes: IPlannedChange[] = missing.map((p, i) => ({
    id: `createFolder:${i}:${p}`, type: 'createFolder', itemKind: 'folder', targetId: '', suggestedParentPath: p,
    needsInput: false, reason: 'Folder for the firm\'s files', confidence: 1, issueIds: []
  }));
  const plan: IOrganizationPlan = { changes, quarantinePath: '' };
  const engine: ChangeEngine = new ChangeEngine({ scan, plan, writer: new GraphService(graph).createDriveWriter(workspace.driveId), audit, user });
  const resolved = changes.map(c => resolveChange(scan, c, { status: 'approved' }));
  const checks = await engine.dryRun(resolved);
  const problems: string[] = checks.filter(c => !c.ready).map(c => c.problems.join(' '));
  if (problems.length) {
    throw new Error(problems[0]);
  }
  return engine.apply(resolved);
}
